import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { gsap, ScrollTrigger } from '@/lib/gsap'
import { prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'
import { usePageTransition } from '@/components/motion/transition-context'

interface RollingNumberProps {
    value: string
    className?: string
    delay?: number
    /** "view" rolls when scrolled into view, "enter" when the page opens. */
    trigger?: 'view' | 'enter'
}

const DIGITS = Array.from({ length: 20 }, (_, index) => index % 10)

const restingPercent = (digit: number): number => -((10 + digit) / DIGITS.length) * 100

/** Odometer style counter: every digit spins a full turn before landing on its value. */
const RollingNumber: FC<RollingNumberProps> = ({ value, className, delay = 0, trigger = 'view' }): ReactNode => {
    const { ready, enterDelay } = usePageTransition()
    const root = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        const element = root.current

        if (!ready || !element || prefersReducedMotion()) return

        const tracks = Array.from(element.querySelectorAll<HTMLElement>('[data-track]'))

        const roll = (): void => {
            gsap.fromTo(tracks, { y: 0, yPercent: 0 }, {
                yPercent: (index: number) => restingPercent(Number(tracks[index].dataset.track)),
                duration: 2.2,
                stagger: 0.12,
                delay,
                ease: 'expo.out'
            })
        }

        if (trigger === 'enter') {
            const call = gsap.delayedCall(enterDelay + 0.2, roll)

            return () => {
                call.kill()
            }
        }

        const scrollTrigger = ScrollTrigger.create({
            trigger: element,
            start: 'top 90%',
            once: true,
            onEnter: roll
        })

        return () => scrollTrigger.kill()
    }, [ready, enterDelay, trigger, delay])

    return (
        <span ref={root} className={cn('inline-flex leading-none', className)}>
            <span className='sr-only'>
                {value}
            </span>

            <span aria-hidden='true' className='inline-flex'>
                {Array.from(value).map((char, index) => {
                    const digit = Number.parseInt(char, 10)

                    if (Number.isNaN(digit)) {
                        return (
                            <span key={index} className='inline-block'>
                                {char}
                            </span>
                        )
                    }

                    return (
                        <span key={index} className='relative inline-block h-[1em] overflow-hidden'>
                            <span
                                data-track={digit}
                                className='flex flex-col will-change-transform'
                                style={{ transform: `translateY(${restingPercent(digit)}%)` } as CSSProperties}
                            >
                                {DIGITS.map((item, itemIndex) => (
                                    <span key={itemIndex} className='block h-[1em] leading-none'>
                                        {item}
                                    </span>
                                ))}
                            </span>
                        </span>
                    )
                })}
            </span>
        </span>
    )
}

export default RollingNumber
